// Servicio para comunicación con el backend
class ApiService {
  constructor() {
    this.baseURL = process.env.NODE_ENV === 'production' 
      ? 'https://dbtarifario.tnf.cl' 
      : 'http://localhost:5000';
  }

  // Detectar tenant del dominio actual (prioridad sobre localStorage)
  getTenantFromDomain() {
    // PRIORIDAD 1: Query param (override temporal)
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');
    if (tenantParam) {
      return tenantParam;
    }
    
    // PRIORIDAD 2: Tenant guardado manualmente (selección del usuario)
    const storedTenant = localStorage.getItem('current_tenant');
    if (storedTenant) {
      return storedTenant;
    }
    
    // PRIORIDAD 3: Detección automática por dominio
    const hostname = window.location.hostname;
    if (hostname.includes('chirak.pe')) {
      return 'chirakPeru';
    } else if (hostname.includes('chirak.cl')) {
      return 'chirakChile';
    }
    
    // PRIORIDAD 4: Default
    return 'chirakChile';
  }

  // Método para hacer requests con manejo de errores
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}/api${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Agregar token si existe
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Agregar tenant detectado del dominio (no solo localStorage)
    const currentTenant = this.getTenantFromDomain();
    config.headers['X-Tenant'] = currentTenant;

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          error: `HTTP ${response.status}: ${response.statusText}` 
        }));
        
        // Para errores de autenticación, devolver el objeto completo con todos los detalles
        if (response.status === 401 || response.status === 403) {
          return {
            success: false,
            error: errorData.error || errorData.message || 'Error de autenticación',
            message: errorData.message,
            availableCompanies: errorData.availableCompanies,
            statusCode: response.status
          };
        }
        
        // Para otros errores, crear un error con los datos del servidor
        const error = new Error(errorData.error || errorData.message || 'Error en la petición');
        error.statusCode = response.status;
        error.data = errorData;
        throw error;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // AUTH ENDPOINTS
  async login(identifier, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password }),
    });
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async refreshToken() {
    return this.request('/auth/refresh', {
      method: 'POST',
    });
  }

  // COMPANY ENDPOINTS
  async getCompanies() {
    try {
      return this.request('/companies');
    } catch (error) {
      console.warn('Error fetching companies from API, using fallback data:', error);
      // Fallback data si falla la API
      return {
        companies: [
          { 
            id: 'chirakChile', 
            name: 'Chirak Turismo Chile',
            country: 'Chile',
            active: true
          },
          { 
            id: 'chirakPeru', 
            name: 'Chirak Turismo Perú',
            country: 'Perú', 
            active: true
          }
        ]
      };
    }
  }

  async getCompanyByDomain(domain) {
    return this.request(`/companies/domain/${domain}`);
  }

  async updateCompany(companyId, data) {
    return this.request(`/companies/${companyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // USER ENDPOINTS
  async getUsers(companyId = null) {
    const endpoint = companyId ? `/users?companyId=${companyId}` : '/users';
    return this.request(endpoint);
  }

  async createUser(userData) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(userId, userData) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(userId, permanent = false) {
    const query = permanent ? '?permanent=true' : '';
    return this.request(`/users/${userId}${query}`, {
      method: 'DELETE',
    });
  }

  async patchUserStatus(userId, statusData) {
    return this.request(`/users/${userId}/status`, {
      method: 'PATCH',
      body: JSON.stringify(statusData),
    });
  }

  // FILE UPLOAD ENDPOINT
  async uploadFile(file, uploadType = 'avatars') {
    console.log('📤 Preparando upload de archivo:', {
      name: file.name,
      type: file.type,
      size: file.size,
      uploadType: uploadType
    });

    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('authToken');
    const url = `${this.baseURL}/api/upload?type=${uploadType}`;

    console.log('🔗 Enviando a:', url);
    console.log('🔑 Token:', token ? 'Presente' : 'Ausente');

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData, // No establecer Content-Type, el navegador lo hace automáticamente con boundary
      });

      console.log('📥 Respuesta del servidor:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        console.error('❌ Error del servidor:', error);
        throw new Error(error.message || `Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Upload exitoso:', result);
      return result;
    } catch (error) {
      console.error('❌ Error en uploadFile:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  async updateUserCompanyRole(userId, companyId, role) {
    return this.request(`/users/${userId}/companies/${companyId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  // CATEGORY ENDPOINTS
  async getCategories(companyId = null, includeInactive = false) {
    let endpoint = '/categories';
    const params = [];
    if (companyId) params.push(`companyId=${companyId}`);
    if (includeInactive) params.push('includeInactive=true');
    if (params.length > 0) endpoint += '?' + params.join('&');
    return this.request(endpoint);
  }

  async createCategory(categoryData) {
    return this.request('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
  }

  async updateCategory(categoryId, categoryData) {
    return this.request(`/categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    });
  }

  async deleteCategory(categoryId, reassignCategoryId = null) {
    const body = reassignCategoryId ? JSON.stringify({ reassign_category_id: reassignCategoryId }) : undefined;
    return this.request(`/categories/${categoryId}`, {
      method: 'DELETE',
      ...(body && { body })
    });
  }

  // PRODUCT ENDPOINTS
  async getProducts(categoryId = null, companyId = null, includeInactive = false) {
    const params = new URLSearchParams();
    if (categoryId) params.append('categoryId', categoryId);
    if (companyId) params.append('companyId', companyId);
    if (includeInactive) params.append('includeInactive', 'true');
    
    const endpoint = `/products${params.toString() ? `?${params.toString()}` : ''}`;
    return this.request(endpoint);
  }

  async createProduct(productData) {
    return this.request('/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
  }

  async updateProduct(productId, productData) {
    return this.request(`/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
  }

  async deleteProduct(productId) {
    return this.request(`/products/${productId}`, {
      method: 'DELETE',
    });
  }

  // STATISTICS ENDPOINTS
  async getStats() {
    return this.request('/stats');
  }

  async getSalesStats(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    return this.request(`/stats/sales${queryString ? '?' + queryString : ''}`);
  }

  // SLIDER ENDPOINTS
  async getSliders() {
    return this.request('/sliders');
  }

  async getAllSliders() {
    return this.request('/sliders/all');
  }

  async getSlider(id) {
    return this.request(`/sliders/${id}`);
  }

  async createSlider(sliderData) {
    return this.request('/sliders', {
      method: 'POST',
      body: JSON.stringify(sliderData),
    });
  }

  async updateSlider(id, sliderData) {
    return this.request(`/sliders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(sliderData),
    });
  }

  async deleteSlider(id) {
    return this.request(`/sliders/${id}`, {
      method: 'DELETE',
    });
  }

  async reorderSlider(id, newIndex) {
    return this.request(`/sliders/${id}/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ newIndex }),
    });
  }

  // DOWNLOAD TRACKING ENDPOINTS
  async trackDownload(productId, documentName, documentUrl) {
    return this.request('/downloads/track', {
      method: 'POST',
      body: JSON.stringify({
        product_id: productId,
        document_name: documentName,
        document_url: documentUrl
      }),
    });
  }

  async getDownloadStats(days = 30, limit = 10) {
    return this.request(`/downloads/stats?days=${days}&limit=${limit}`);
  }

  async getDownloadStatsAll({ days = 30, months = null } = {}) {
    const params = months ? `months=${months}` : `days=${days}`;
    return this.request(`/downloads/stats/all?${params}`);
  }

  async getDownloadEvolution(days = 30) {
    return this.request(`/downloads/evolution?days=${days}`);
  }

  // ACCESS LOGS
  async getAccessLogs({ page = 1, limit = 20, days = 30, search = '' } = {}) {
    return this.request(`/accesslogs?page=${page}&limit=${limit}&days=${days}&search=${encodeURIComponent(search)}`);
  }

  async getAccessLogStats(days = 30) {
    return this.request(`/accesslogs/stats?days=${days}`);
  }

  async getAccessLogsAll({ days = 30, search = '' } = {}) {
    return this.request(`/accesslogs/all?days=${days}&search=${encodeURIComponent(search)}`);
  }

  // EDIT LOGS
  async getEditLogs({ page = 1, limit = 20, days = 30, search = '', entity_type = '' } = {}) {
    return this.request(`/editlogs?page=${page}&limit=${limit}&days=${days}&search=${encodeURIComponent(search)}&entity_type=${entity_type}`);
  }

  async getEditLogsAll({ days = 30, search = '', entity_type = '' } = {}) {
    return this.request(`/editlogs/all?days=${days}&search=${encodeURIComponent(search)}&entity_type=${entity_type}`);
  }
}

// Instancia singleton del servicio
const apiService = new ApiService();
export default apiService;